import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { ApiRequestError, errorMessage } from '@/lib/api-client';
import type { PatientDetail, Visit } from '@/types/api';
import { visitsApi } from '../api';

/**
 * Confirming a visit with its price (or changing the price later), marking it
 * "Fiskal", and focusing the price field when dictation or typing ends.
 */
export function useVisitSignOff(
  setPatient: Dispatch<SetStateAction<PatientDetail | undefined>>,
  recording: boolean,
  /** The visit shown is open (its price field is on screen). */
  open: boolean,
  /** Reload the entries (confirming approves the visit's legacy pending ones). */
  reloadEntries: () => Promise<void>
) {
  const { t } = useTranslation();
  const toast = useToast();
  const priceRef = useRef<HTMLInputElement>(null);

  // A visit just opened by the dictation may still be loading when recording
  // stops: remember to focus its price once it is there.
  const [focusPending, setFocusPending] = useState(false);
  const wasRecording = useRef(false);
  useEffect(() => {
    if (wasRecording.current && !recording) setFocusPending(true);
    if (recording) setFocusPending(false);
    wasRecording.current = recording;
  }, [recording]);
  useEffect(() => {
    if (!(focusPending && open && priceRef.current)) return;
    priceRef.current.focus();
    setFocusPending(false);
  }, [focusPending, open]);

  const onPrice = useCallback(
    async (visit: Visit, priceRsd: number) => {
      try {
        const updated =
          visit.status === 'OPEN'
            ? await visitsApi.confirm(visit.id, priceRsd)
            : await visitsApi.setPrice(visit.id, priceRsd);
        setPatient((p) =>
          p ? { ...p, visits: p.visits.map((v) => (v.id === updated.id ? updated : v)) } : p
        );
        if (visit.status === 'OPEN') {
          toast.show(t('chart.visit.confirmedToast'));
          await reloadEntries();
        }
        return true;
      } catch (err) {
        toast.show(
          err instanceof ApiRequestError && err.isNetwork
            ? t('chart.toast.needsOnline')
            : errorMessage(err),
          'error'
        );
        return false;
      }
    },
    [setPatient, reloadEntries, toast, t]
  );

  const onFiscal = useCallback(
    async (visit: Visit, fiscal: boolean) => {
      try {
        const updated = await visitsApi.setFiscal(visit.id, fiscal);
        setPatient((p) =>
          p ? { ...p, visits: p.visits.map((v) => (v.id === updated.id ? updated : v)) } : p
        );
        return true;
      } catch (err) {
        toast.show(
          err instanceof ApiRequestError && err.isNetwork
            ? t('chart.toast.needsOnline')
            : errorMessage(err),
          'error'
        );
        return false;
      }
    },
    [setPatient, toast, t]
  );

  return { priceRef, onPrice, onFiscal };
}
